/* eslint-disable react/jsx-props-no-spreading */
import { createUseStyles, useDarkMode } from '@glu/theming';
import React, { useCallback, useMemo } from 'react';
import { Loading } from '@glu/indicators-react';
import classnames from 'classnames';
import locale from '@glu/locale';
import Position from './Position';
import Options from './Options/Options';
import FilterInput from './FilterInput';
import Breadcrumbs from './Breadcrumbs';
import Actions from './Actions';
import styles from './Panel.styles';
import { PanelOption, Path } from './Dropdown';
import usePanelState from './usePanelState';
import PanelContext from './PanelContext';

const useStyles = createUseStyles(styles);

export interface PanelActions {
  handleCategorySelect: (option: PanelOption) => void;
  handleScroll: () => void;
  setArrowDirection: (payload: string) => void;
  /** The html value of the filter input itself. */
  setFilterHtmlValue: (payload: string) => void;
  /** The user's typed input which options are filtered by  */
  setFilterString: (payload: string) => void;
  setIsActionsFocused: (payload: boolean) => void;
  /** callback to set the parent name for the breadcrumb option */
  setParentName: (payload: string | number) => void;
  /** Sets the path used in the breadcrumb */
  setPath: (payload: Path[]) => void;
}

const Panel = () => {
  const classes = useStyles();
  const dark = useDarkMode();
  const panelState = usePanelState();

  const {
    applySelection,
    dropdownProps,
    dropdownState,
    endRef,
    filteredOptions,
    formattedOptions,
    handleCategorySelect,
    handleScroll,
    isActionsFocused,
    isFilterFocused,
    paginated,
    scrollRef,
    setArrowDirection,
    setFilterHtmlValue,
    setFilterString,
    setFocusedIndex,
    setIsActionsFocused,
    setIsFilterFocused,
    setParentName,
    setPath,
    setSelection,
    state,
    uniqueId,
  } = panelState;

  const {
    btnClearEnabled,
    btnDoneEnabled,
    btnSelectAllEnabled,
    emptyOptionsMessage = locale.get('noResultsFound'),
    filterBy,
    filterDebounce,
    filterEnabled,
    filterPlaceholder,
    forcePositionY,
    headerComponent: HeaderComponent,
    htmlId,
    isLoading,
    matchBy,
    multiselect,
    onCategorySelect,
    onClear,
    onFilter,
    onSelectAll,
    optionComponent,
    optionListComponent: CustomOptionListComponent,
    options,
    positionX,
    positionY,
    screenReaderLabel,
    scroll,
    showGroups,
  } = dropdownProps;

  const { filterHtmlValue, filterString, path } = state;

  const { focusedIndex, selection } = dropdownState;

  const getOptionListComponent = useCallback(() => {
    const OptionsList = Options;
    if (CustomOptionListComponent) {
      return () => (
        <CustomOptionListComponent
          applySelection={applySelection}
          filterString={filterString}
          focusedIndex={focusedIndex}
          handleCategorySelect={handleCategorySelect}
          htmlId={htmlId}
          multiselect={multiselect}
          optionComponent={optionComponent}
          options={paginated}
          path={path}
          selection={selection}
          setArrowDirection={setArrowDirection}
          setFocusedIndex={setFocusedIndex}
          setIsActionsFocused={setIsActionsFocused}
          setIsFilterFocused={setIsFilterFocused}
          setParentName={setParentName}
          setPath={setPath}
          setSelection={setSelection}
          showGroups={showGroups}
        />
      );
    }
    return OptionsList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    CustomOptionListComponent,
    applySelection,
    filterString,
    focusedIndex,
    htmlId,
    multiselect,
    optionComponent,
    paginated,
    path,
    selection,
    setFocusedIndex,
    setIsFilterFocused,
    setSelection,
    showGroups,
  ]);

  const OptionsList = getOptionListComponent();

  const panelContext = useMemo(
    () => ({
      panelActions: {
        handleCategorySelect,
        handleScroll,
        setArrowDirection,
        setFilterHtmlValue,
        setFilterString,
        setParentName,
        setPath,
        setIsActionsFocused,
      },
      panelState: state,
      dropdownActions: {
        applySelection,
        setFocusedIndex,
        setIsFilterFocused,
        setSelection,
      },
      dropdownState,
      dropdownProps: { ...dropdownProps, options: paginated },
      uniqueId,
    }),
    [
      applySelection,
      dropdownProps,
      dropdownState,
      handleCategorySelect,
      handleScroll,
      setArrowDirection,
      setFilterHtmlValue,
      setFilterString,
      setFocusedIndex,
      setIsActionsFocused,
      setIsFilterFocused,
      setParentName,
      setPath,
      setSelection,
      state,
      uniqueId,
      paginated,
    ]
  );

  const getActiveDescendant = useCallback(
    () => (multiselect ? selection.map((sel) => sel.name).join(' ') : selection?.[0]?.name),
    [multiselect, selection]
  );

  return (
    <PanelContext.Provider value={panelContext}>
      <Position
        className={classes.panel}
        dataQa="panel-position"
        forcePositionY={forcePositionY}
        positionX={positionX}
        positionY={positionY}
        screenReaderLabel={screenReaderLabel}
        scroll={scroll}
      >
        <div
          className={classnames(classes.root, { [classes.dark]: dark })}
          data-qa="dropdown-panel"
          id={`${htmlId}-dropdown-panel`}
        >
          {HeaderComponent && <HeaderComponent />}
          {filterEnabled && (
            <FilterInput
              applySelection={applySelection}
              dark={dark}
              filterBy={filterBy}
              filterDebounce={filterDebounce}
              filterHtmlValue={filterHtmlValue}
              filterPlaceholder={filterPlaceholder}
              filterString={filterString}
              isFocused={isFilterFocused}
              matchBy={matchBy}
              multiselect={multiselect}
              onFilter={onFilter}
              options={formattedOptions}
              selection={selection}
              setArrowDirection={setArrowDirection}
              setFilterHtmlValue={setFilterHtmlValue}
              setFilterString={setFilterString}
              setFocusedIndex={setFocusedIndex}
              setIsFilterFocused={setIsFilterFocused}
              setSelection={setSelection}
            />
          )}
          <div
            ref={scrollRef}
            aria-activedescendant={getActiveDescendant()}
            className={classes.scroll}
            data-qa="scroll-panel"
            id={`${htmlId}-scroll-panel`}
            onScroll={handleScroll}
            role="listbox"
            tabIndex={0}
          >
            <Breadcrumbs
              dark={dark}
              onStepSelect={onCategorySelect}
              path={path}
              setParentName={setParentName}
              setPath={setPath}
            />
            {options.length ? (
              <>
                <OptionsList />
                <Loading
                  className={classes.loading}
                  data-qa="loading-spinner"
                  isLoading={isLoading}
                  size={16}
                >
                  <div ref={endRef} className={classes.end} />
                  {filteredOptions?.length === 0 && (
                    <div className={classnames(classes.noresults, { [classes.dark]: dark })}>
                      {emptyOptionsMessage}
                    </div>
                  )}
                </Loading>
              </>
            ) : (
              <Loading className={classes.loading} isLoading={isLoading} size={16}>
                <div className={classnames(classes.noresults, { [classes.dark]: dark })}>
                  {emptyOptionsMessage}
                </div>
              </Loading>
            )}
          </div>
          {multiselect && (
            <Actions
              applySelection={applySelection}
              btnClearEnabled={btnClearEnabled}
              btnDoneEnabled={btnDoneEnabled}
              btnSelectAllEnabled={btnSelectAllEnabled}
              focused={isActionsFocused}
              onClear={onClear}
              onSelectAll={onSelectAll}
              options={filteredOptions}
              selection={selection}
              setSelection={setSelection}
            />
          )}
        </div>
      </Position>
    </PanelContext.Provider>
  );
};

export default Panel;
